import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { ALL_FACILITIES, useFacilitiesQuery } from '@/hooks/useFacilitiesQuery';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { PostHog } from '@/lib/PostHog';
import { RoutePath } from '@/routes/RoutePath';

import { useFacilitySelectorController } from './useFacilitySelectorController';
import {
  ALL_FACILITIES_PARAM,
  allFacilitiesIntentAtom,
  FACILITY_TYPE_PARAM,
  isValidFacilityType,
  SCREENER_FACILITY_VIEW_KIND,
  SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT,
  SET_FACILITY_ID_PARAM,
  useScreenerFacilityView,
  useUserCanUseAllFacilities
} from './useScreenerFacilityView';
import { useWarnOnLeavingAllFacilitiesSupportedRoute } from './useWarnOnLeavingAllFacilitiesSupportedRoute';

const getFallbackFacilityId = (
  currentUser: ReturnType<typeof useCurrentUser>['data']
) => {
  if (!currentUser?.user_facility_accesses?.length) {
    return null;
  }
  const homeAccess = currentUser.user_facility_accesses.find(
    (access) => access.facility_id === currentUser.facility_id
  );
  return (
    homeAccess?.facility_id ??
    currentUser.user_facility_accesses[0]?.facility_id ??
    null
  );
};

/**
 * Side-effects backing the screener facility-selection system. Mount once at
 * the layout level (regardless of whether the FacilitySelector itself is
 * rendered) so URL params are always consumed and the warn snackbar always
 * fires.
 */
export const useFacilitySelectorEffects = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser().data;
  const { view, selectedFacilityId, setFacilityId } = useScreenerFacilityView();
  const { selectFacility } = useFacilitySelectorController();
  const [, setIntent] = useAtom(allFacilitiesIntentAtom);
  const userCanUseAllFacilities = useUserCanUseAllFacilities();
  const { data: facilitiesResponse } =
    useFacilitiesQuery().useFindAllWithCanSelectAllFlag();
  const facilities = facilitiesResponse?.facilities;
  const isMobileViewport = useIsMobileViewport();

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    const accesses = currentUser.user_facility_accesses ?? [];
    const hasValidSelection =
      (selectedFacilityId === ALL_FACILITIES && !isMobileViewport) ||
      (!!selectedFacilityId &&
        accesses.some((access) => access.facility_id === selectedFacilityId));
    if (hasValidSelection) {
      return;
    }
    const fallbackId = getFallbackFacilityId(currentUser);
    if (fallbackId) {
      setFacilityId(fallbackId);
    }
  }, [selectedFacilityId, currentUser, setFacilityId, isMobileViewport]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const setFacilityIdParam = params.get(SET_FACILITY_ID_PARAM);
    const allFacilitiesParam = params.get(ALL_FACILITIES_PARAM);

    const isOnAllFacilitiesSupportedRoute =
      SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT.includes(
        location.pathname as RoutePath
      );

    const hasFacilityParams =
      !!setFacilityIdParam || allFacilitiesParam === 'true';

    if (!hasFacilityParams) {
      return;
    }

    const consumeAllFacilities =
      userCanUseAllFacilities &&
      isOnAllFacilitiesSupportedRoute &&
      allFacilitiesParam === 'true' &&
      !setFacilityIdParam;

    const consumeSetFacilityId =
      !!setFacilityIdParam && setFacilityIdParam !== selectedFacilityId;

    if (consumeSetFacilityId && setFacilityIdParam) {
      selectFacility(setFacilityIdParam);
    } else if (consumeAllFacilities) {
      const rawType = params.get(FACILITY_TYPE_PARAM);
      if (isValidFacilityType(rawType)) {
        setIntent({ facilityType: rawType });
      } else {
        setIntent(null);
      }
    }

    params.delete(SET_FACILITY_ID_PARAM);
    params.delete(ALL_FACILITIES_PARAM);
    params.delete(FACILITY_TYPE_PARAM);
    const remaining = params.toString();
    navigate(
      remaining ? `${location.pathname}?${remaining}` : location.pathname,
      { replace: true, state: location.state }
    );
  }, [
    currentUser,
    location.pathname,
    location.search,
    location.state,
    navigate,
    selectFacility,
    selectedFacilityId,
    setIntent,
    userCanUseAllFacilities
  ]);

  useEffect(() => {
    if (view.kind === SCREENER_FACILITY_VIEW_KIND.AllFacilities) {
      PostHog.setFacility({
        scope: 'all',
        facilityId: null,
        facilityType: view.facilityType
      });
      return;
    }

    if (!selectedFacilityId || selectedFacilityId === ALL_FACILITIES) {
      PostHog.setFacility({
        scope: 'single',
        facilityId: null,
        facilityType: null
      });
      return;
    }

    const facility = facilities?.find((f) => f.id === selectedFacilityId);
    PostHog.setFacility({
      scope: 'single',
      facilityId: selectedFacilityId,
      facilityType: facility?.facility_type ?? null
    });
  }, [view.kind, view.facilityType, selectedFacilityId, facilities]);

  useWarnOnLeavingAllFacilitiesSupportedRoute({
    pathname: location.pathname,
    isInAllFacilitiesView: view.kind === 'all-facilities',
    selectedFacilityType: view.facilityType
  });
};
